import { useId, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useT, type DictionaryKey } from '@dia/i18n';
import { useSyncStatus } from '@dia/db';
import { LangToggle, Screen, SyncBadge, cn } from '@dia/ui';
import { sync } from '../../supabase';
import { Icon, type IconName } from './icons';

/**
 * Small pieces every ops screen shares. Sunlight mode has an 18 px floor, and
 * the caption size in the ops preset is now 18 px too, so the header parts
 * arrive at the right size and this wrapper no longer has to lift them.
 */

export function HeaderActions({ badge = true }: { badge?: boolean } = {}) {
  const status = useSyncStatus(sync);
  return (
    // Wrapping, not holding the line (defect D9). At 200 percent zoom a 390 pt
    // phone is a 195 px viewport and the badge plus the toggle are wider than
    // that, so a row that refused to wrap scrolled every ops screen sideways.
    <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
      {/* Nothing has been written yet on the sign in, owner sign in and pairing
          screens, so "everything is sent" there is noise. They pass badge={false}. */}
      {badge && (
        <SyncBadge pending={status.pending} dead={status.dead} online={status.online} syncing={status.syncing} />
      )}
      <LangToggle />
    </div>
  );
}

const TABS: { to: string; icon: IconName; label: DictionaryKey }[] = [
  { to: '/', icon: 'home', label: 'ops.nav.today' },
  { to: '/limpieza', icon: 'broom', label: 'ops.nav.cleaning_short' },
  { to: '/mantenimiento', icon: 'alert', label: 'ops.nav.report_short' },
  { to: '/sistemas', icon: 'book', label: 'ops.nav.systems' },
  { to: '/mas', icon: 'dots', label: 'ops.nav.more' },
];

export function TabBar() {
  const { t } = useT();
  return (
    // Five equal columns, not a narrow-wide-wide-wide-narrow split: the
    // uneven track widths (Hoy and Mas pinned to 3.5rem so the three longer
    // labels got the rest) read as accidental rather than designed, and it
    // got worse once "Reportar un problema" replaced the short "Reportar".
    // The label already wraps and hyphenates at any width (defect R2), so
    // giving every tab the same width costs nothing.
    <nav aria-label={t('ops.nav.label')} className="-mx-2 grid grid-cols-5">
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.to === '/'}
          className={({ isActive }) =>
            cn(
              // min-w-0 so a grid track can be narrower than its word at
              // 200 percent zoom, instead of pushing the bar off the screen.
              'flex min-w-0 min-h-tap flex-col items-center justify-center gap-0.5 rounded px-0 py-1 text-body leading-tight',
              isActive ? 'bg-accent text-onAccent font-semibold' : 'text-ink',
            )
          }
        >
          {/* Report keeps its own color, active tab or not: a plain warning
              triangle reads as "something might be wrong here" at a glance,
              which the accent color (used for every other active tab) would
              wash out. */}
          <span className={tab.icon === 'alert' ? 'text-danger' : undefined}>
            <Icon name={tab.icon} />
          </span>
          {/* anywhere, not break-word: a grid track at 200 percent zoom is
              narrower than "Limpieza", and break-word let the words run into
              each other instead of wrapping (seen on Hoy once it became home). */}
          <span className="min-w-0 max-w-full text-center [overflow-wrap:anywhere] hyphens-auto">{t(tab.label)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function Page({
  title,
  back,
  nav = true,
  children,
  footer,
}: {
  title: string;
  /** Where the back word goes. Omit on a top level screen. */
  back?: string;
  nav?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  return (
    <Screen
      title={title}
      actions={<HeaderActions />}
      footer={
        <>
          {footer}
          {nav && <TabBar />}
        </>
      }
    >
      {back && (
        <button
          type="button"
          onClick={() => navigate(back)}
          className="mb-3 inline-flex min-h-tap items-center gap-1 text-accent underline underline-offset-4"
        >
          {t('action.back')}
        </button>
      )}
      {children}
    </Screen>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn('rounded border border-line bg-surface p-4', className)}>{children}</section>;
}

export function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral'; children: ReactNode }) {
  const tones = {
    ok: 'text-ok border-ok',
    warn: 'text-warn border-warn',
    danger: 'text-danger border-danger',
    info: 'text-info border-info',
    neutral: 'text-inkMuted border-line',
  };
  return (
    <span
      className={cn(
        // max-w-full and a word that breaks: a pill wider than the viewport at
        // 200 percent zoom used to carry the whole screen sideways (R2).
        'inline-flex max-w-full items-center break-words rounded border bg-surface px-2 py-1 text-body font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/** A label with its input under it, at full size. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

export const inputClass =
  'min-h-tap w-full rounded border border-line bg-surface px-3 py-2 text-ink placeholder:text-inkFaint';

/**
 * A password box with a Show / Hide button beside it, so what you type can be
 * read instead of a row of dots. The button carries a word as well as the icon
 * (Section 9). The caller owns `revealed`, so two boxes can share one switch.
 * No length or character rules live here.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  revealed,
  onToggle,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  revealed: boolean;
  onToggle: () => void;
}) {
  const { t } = useT();
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          className={`${inputClass} min-w-0 flex-1`}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={revealed}
          className="inline-flex min-h-tap shrink-0 items-center gap-2 rounded border border-line bg-surface px-3 text-ink"
        >
          <Icon name={revealed ? 'eyeOff' : 'eye'} />
          <span>{revealed ? t('ops.owner.hide') : t('ops.owner.show')}</span>
        </button>
      </div>
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger' | 'ok'; children: ReactNode }) {
  const tones = { info: 'border-info', warn: 'border-warn', danger: 'border-danger', ok: 'border-ok' };
  return (
    <p role={tone === 'danger' ? 'alert' : 'status'} className={cn('rounded border-l-4 bg-surface p-3', tones[tone])}>
      {children}
    </p>
  );
}

/**
 * The card lift the old launcher tiles had: a real border as well as a
 * shadow, because a shadow is the first thing jungle glare washes out and the
 * border still draws the edge when it does.
 */
const LIFT =
  'shadow-[0_1px_2px_rgb(var(--c-ink)/0.08),0_6px_16px_-6px_rgb(var(--c-ink)/0.22)] ' +
  'hover:shadow-[0_2px_4px_rgb(var(--c-ink)/0.08),0_12px_24px_-8px_rgb(var(--c-ink)/0.28)] ' +
  'active:shadow-[0_1px_1px_rgb(var(--c-ink)/0.10)]';

/**
 * A big tappable row that goes somewhere, with an icon and a word.
 *
 * The icon sits in a soft accent badge, and a finger that presses the row
 * sees it answer on its own (no hover on a phone): the edge turns accent and
 * the badge fills solid. The words break rather than hang over the edge at
 * 200 percent zoom (defect R2), and min-w-0 lets the text column shrink.
 */
export function NavRow({ to, icon, title, hint }: { to: string; icon: IconName; title: string; hint?: string }) {
  return (
    <NavLink
      to={to}
      className={cn(
        'group flex min-h-[64px] min-w-0 items-center gap-3 rounded border border-line bg-surface p-3 text-ink no-underline min-[320px]:p-4',
        LIFT,
        'transition-[box-shadow,border-color,background-color] duration-150 ease-out motion-reduce:transition-none',
        'hover:border-accent active:border-accent active:bg-accent/5',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      )}
    >
      <span
        className={cn(
          'grid h-12 w-12 shrink-0 place-items-center rounded-[0.875rem] bg-accent/10 text-accent',
          'transition-colors duration-150 group-hover:bg-accent/15 group-active:bg-accent group-active:text-onAccent',
          'motion-reduce:transition-none',
        )}
      >
        <Icon name={icon} size={28} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="break-words font-semibold hyphens-auto">{title}</span>
        {hint && <span className="break-words text-inkMuted hyphens-auto">{hint}</span>}
      </span>
    </NavLink>
  );
}
